/**
 * useBoardColumns(monday, itemId, boardId?) / useBoardMembers(monday, itemId, boardId?)
 *
 * useBoardColumns fetches the item's subitems board with column settings
 * (`settings`, API 2025-10+) — this is what populates the Add-column list and
 * every cell editor (REQ-6.1). useBoardMembers fetches the parent board's members for the
 * people picker, lazily, only when a People cell is opened.
 *
 * Settings are parsed defensively per type; an unparseable blob degrades that
 * column's editor (no colours / options), never blocks the editor.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { canonicalType } from '../Config';
import { fetchSubitemsBoard, fetchSubitemsBoardOfBoard } from './subitemsBoard';

export function parseColumnSettings(type, raw) {
  if (!raw) return undefined;
  let s = raw;
  if (typeof raw === 'string') {
    try {
      s = JSON.parse(raw);
    } catch (e) {
      return undefined;
    }
  }
  if (!s || typeof s !== 'object') return undefined;

  try {
    switch (canonicalType(type)) {
      case 'status': {
        // `settings`, seen 2026-10-14:
        //   { labels: [{ id: 1, label: 'Done', index: 2, hex: '#00c875', color: 1, is_deactivated: false }] }
        // `id` is the label's VALUE (what {"index": n} in a column value means);
        // `index` is only its display position, `color` a palette number, `hex`
        // the colour. Deactivated labels cannot be picked in monday: left out.
        const out = (Array.isArray(s.labels) ? s.labels : [])
          .filter((l) => l && !l.is_deactivated)
          .map((l) => ({ index: Number(l.id), label: l.label, color: l.hex || '#c4c4c4', position: Number(l.index) }))
          .filter((l) => Number.isFinite(l.index) && l.label !== undefined && l.label !== null)
          .sort((a, b) => (Number.isFinite(a.position) && Number.isFinite(b.position) ? a.position - b.position : a.index - b.index))
          .map(({ position, ...l }) => l);
        return out.length ? { labels: out } : undefined;
      }
      case 'dropdown': {
        // `settings`, seen 2026-10-14: { labels: [{ id, label, is_deactivated }] }
        const options = (Array.isArray(s.labels) ? s.labels : [])
          .filter((l) => l && !l.is_deactivated)
          .map((l) => ({ id: l.id, name: l.label }))
          .filter((l) => l.name);
        return options.length ? { options } : undefined;
      }
      case 'rating': {
        // { limit: 3..10, symbol: 'stars'|'hearts', color: '#hex' }
        // https://developer.monday.com/api-reference/reference/rating
        const limit = s.limit ?? s.max ?? s.max_rating ?? s.count;
        const out = {};
        if (limit !== undefined && limit !== null) out.limit = Number(limit);
        if (s.symbol || s.symbol_name || s.icon) out.symbol = String(s.symbol ?? s.symbol_name ?? s.icon);
        if (s.color) out.color = s.color;
        return Object.keys(out).length ? out : undefined;
      }
      case 'numbers': {
        const unit =
          (s.unit && (s.unit.symbol || s.unit.custom_unit)) ||
          (typeof s.unit === 'string' ? s.unit : null);
        return unit ? { unit } : undefined;
      }
      default:
        return undefined;
    }
  } catch (e) {
    return undefined;
  }
}

/**
 * A column's parsed settings, from `settings` (JSON, API 2025-10+). Every type
 * parsed above was compared with the deprecated settings_str on real boards
 * (diagnostics, 2026-10-14) before settings_str was dropped.
 */
export function columnSettingsOf(column) {
  return column ? parseColumnSettings(column.type, column.settings) : undefined;
}

/** The column fields every board read asks for. */
export const COLUMN_SETTINGS_FIELDS = 'settings';

/**
 * Merge a board's subscribers and owners into one de-duplicated member list.
 * Owners are not guaranteed to appear in `subscribers`, so both are unioned.
 * Pure — unit tested in boardMembers.test.js.
 */
export function mergeBoardMembers(subscribers, owners) {
  const byId = new Map();
  for (const u of [...(subscribers || []), ...(owners || [])]) {
    if (!u || u.id === undefined || u.id === null) continue;
    const id = String(u.id);
    if (!byId.has(id)) byId.set(id, { id: u.id, name: u.name || String(u.id) });
  }
  return [...byId.values()];
}

/** Flatten whatever shape monday used to report failure into one readable string. */
export function describeApiErrors(res) {
  const raw = res?.errors ?? res?.error_message ?? res?.errorMessage;
  if (!raw) return null;
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw)) {
    const msgs = raw.map((e) => (typeof e === 'string' ? e : e?.message || JSON.stringify(e)));
    // monday repeats one error per row it resolved; say each once
    return [...new Set(msgs.filter(Boolean))].join('; ') || 'unknown API error';
  }
  return raw?.message || JSON.stringify(raw);
}

/**
 * One monday query → `{ data }` or `{ error }`.
 *
 * CRITICAL: monday answers a rejected GraphQL query with **HTTP 200 and an
 * `errors[]` body**, and lib/mondayApi keeps that envelope, so `monday.api(...)`
 * RESOLVES on failure and a bare try/catch never fires. Every caller must inspect
 * the payload, or a missing scope looks exactly like an empty result. That is
 * what made the people picker silently show "No members found" instead of
 * naming the real problem.
 */
async function queryOrError(monday, query) {
  let res;
  try {
    res = await monday.api(query);
  } catch (e) {
    return { error: (e && e.message) || 'request failed' };
  }
  const error = describeApiErrors(res);
  if (error) return { error };
  return { data: res?.data };
}

/**
 * Members for the people picker: BOARD MEMBERS ONLY, from the PARENT board.
 *
 * `items(ids:X) { board { … } }` resolves to the board the ITEM lives on — the
 * parent board — which is where people are actually subscribed. (Contrast
 * fetchSubitemsBoard, which goes through `subitems { board }` to reach the
 * subitems board.) A subitems board has no meaningful member list of its own.
 *   subscribers — "The board's subscribers."
 *   owners      — "The board's owners."
 * https://developer.monday.com/api-reference/reference/boards
 *
 * `team_subscribers` is deliberately NOT requested. It needs a scope this app
 * does not hold, and monday fails the WHOLE query when a single field is
 * unauthorized, so asking for it returned zero members in production:
 *   Unauthorized to load field 'Query.items.board.team_subscribers',
 *   Reason: missing required scopes.  (code UNAUTHORIZED_FIELD_OR_TYPE)
 * It was only used to spot the "everyone at account" team (id -1) and then widen
 * the list to every account user. The fallback below covers that case anyway:
 * a board shared only with that team has no individual subscribers to read, and
 * an empty read triggers the account list.
 *
 * Degrades in two steps:
 *   1. parent board subscribers + owners;
 *   2. if that errored — or returned nothing, which for a board that always has
 *      at least an owner means we could not read it — fall back to the account
 *      user list (needs `users:read`, but as a separate query, so it can succeed
 *      even when step 1 is refused).
 * The error from whichever step failed is returned so the picker can name it
 * instead of pretending the board is empty.
 *
 * @returns {{members: Array, error: string|null}}
 */
export async function fetchBoardMembers(monday, itemId, boardId) {
  // a board view has no item: the same members, read off the board itself
  const boardRes = itemId
    ? await queryOrError(monday, `query { items(ids:${itemId}) { board { id subscribers { id name } owners { id name } } } }`)
    : await queryOrError(monday, `query { boards(ids:${boardId}) { id subscribers { id name } owners { id name } } }`);

  const board = itemId ? boardRes.data?.items?.[0]?.board : boardRes.data?.boards?.[0];
  const members = board ? mergeBoardMembers(board.subscribers, board.owners) : [];

  if (members.length) return { members, error: null };

  // The query failed, or it came back empty — for a board that always has at
  // least an owner, empty means we could not read it.
  const usersRes = await queryOrError(
    monday,
    `query { users (limit: 200, kind: non_guests) { id name } }`,
  );
  const accountUsers = usersRes.data?.users;
  if (Array.isArray(accountUsers) && accountUsers.length) {
    return { members: mergeBoardMembers(members, accountUsers), error: null };
  }

  const error = boardRes.error || usersRes.error || null;
  if (error) {
    console.error('Could not load board members for the people picker:', error);
  }
  return { members, error };
}

/** The subitems board's columns, reached from the item — or, in a board view, from the board (boardId). */
export default function useBoardColumns(monday, itemId, boardId) {
  const [state, setState] = useState({
    loading: true,
    error: null,
    boardId: null,
    itemName: '',
    columns: [],
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Only the board gates the editor. Members are fetched lazily by
        // useBoardMembers, the first time a People cell is opened.
        const { board, itemName = '' } = itemId
          ? await fetchSubitemsBoard(monday, itemId)
          : await fetchSubitemsBoardOfBoard(monday, boardId);
        if (!alive) return;
        const columns = (board.columns || [])
          .filter((c) => c.type !== 'name')
          .map((c) => ({
            id: c.id,
            title: c.title,
            type: c.type,
            settings: columnSettingsOf(c),
          }));
        setState({
          loading: false,
          error: null,
          boardId: board.id,
          itemName,
          columns,
        });
      } catch (e) {
        console.error('Could not load the subitems board columns', e);
        if (!alive) return;
        setState((prev) => ({ ...prev, loading: false, error: e }));
      }
    })();
    return () => {
      alive = false;
    };
  }, [monday, itemId, boardId]);

  return state;
}

/**
 * useBoardMembers(monday, itemId, boardId?) → { status, users, error, load }
 *
 * The people picker's member list, fetched ON DEMAND: only the People cell editor
 * needs it, so it no longer delays opening the editor. `load()` is called when a
 * People cell opens; it is a no-op while a fetch is in flight or once members are
 * in hand, so every later People cell opens instantly from the cached list. A
 * fetch that ended with no members (an error, or nobody readable) is retried on
 * the next open.
 *
 *   status: 'idle' | 'loading' | 'loaded'
 */
export function useBoardMembers(monday, itemId, boardId) {
  const [state, setState] = useState({ status: 'idle', users: [], error: null });
  // Bumped on unmount and on item change, so a response for an old item (or an
  // unmounted editor) is dropped instead of landing in the new state.
  const generation = useRef(0);
  const inFlight = useRef(false);

  useEffect(() => {
    generation.current += 1;
    inFlight.current = false;
    setState({ status: 'idle', users: [], error: null });
    return () => {
      generation.current += 1;
    };
  }, [monday, itemId, boardId]);

  const load = useCallback(() => {
    if (inFlight.current) return;
    if (state.status === 'loaded' && state.users.length) return;
    const gen = generation.current;
    inFlight.current = true;
    setState((prev) => ({ ...prev, status: 'loading' }));
    fetchBoardMembers(monday, itemId, boardId)
      .then(
        ({ members, error }) => ({ users: members, error }),
        (e) => ({ users: [], error: (e && e.message) || 'request failed' }),
      )
      .then(({ users, error }) => {
        if (gen !== generation.current) return;
        inFlight.current = false;
        setState({ status: 'loaded', users, error });
      });
  }, [monday, itemId, boardId, state.status, state.users.length]);

  return { ...state, load };
}
